import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle, ClipboardCheck, Clock, Download, Loader2, Search, UserCheck, UserMinus, UserX, Users, type LucideIcon } from 'lucide-react';
import LmsEditorHeader from '../../components/LmsEditorHeader';
import LmsSessionAttendanceModal from '../../components/LmsSessionAttendanceModal';
import { InlineLoader } from '../../components/loading';
import { lmsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useTableSort, SortTh } from '../../components/dashboard/useTableSort';
import { downloadTableXlsx, fileSlug, type TableCell } from '../../utils/tableXlsx';

/**
 * Grow → Training Sessions → Learners.
 *
 * Who a training session was assigned to, and where each of them stands: not
 * registered, registered, attended, or did not attend. The sessions list only
 * shows two counts, and the Attendance window only lists people who registered;
 * neither answers "who has not registered yet".
 *
 * It is also where the trainer settles attendance, one person at a time. A learner
 * who checks in on their phone is Attended straight away, with the session's
 * points, and is then "awaiting approval": the trainer approves it, or rejects it
 * (the learner becomes Absent and the points go back). Somebody who did not check
 * in can be marked attended or absent here. The Attendance window is still there
 * for ratings, "excused" and marking a whole register in one go.
 *
 * Marking somebody absent or excused does not shut them out. If they have no
 * check-in on record, they may still check in once on their phone while the
 * session is running. That check-in comes back here as Attended and awaiting
 * approval, with `marked_before` saying what the trainer had marked. The row says
 * so, and "Approve all" leaves it out: the trainer who marked the person absent
 * decides that one on its own. Rejecting a check-in is final.
 */

type RosterState = 'not_registered' | 'registered' | 'attended' | 'absent' | 'excused' | 'cancelled' | 'no_show';
/** Where a learner's own check-in stands with the trainer; null when they did not check in themselves. */
type AttendanceReview = 'awaiting' | 'approved' | 'rejected';

interface RosterRow {
  employee_code: string;
  employee_name: string;
  store: string;
  city: string;
  assigned_via: string[];
  state: RosterState;
  registered_at: string | null;
  attended_at: string | null;
  attendance_source: 'self' | 'admin' | null;
  check_in_at: string | null;
  points_awarded: number;
  // Absent from a server that predates trainer approval.
  review?: AttendanceReview | null;
  /** Who approved, rejected or marked the attendance, and when. */
  decided_by?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  /**
   * What the trainer had marked before this person checked in: set only when the
   * check-in FOLLOWED an absent / excused mark. Absent from an older server.
   */
  marked_before?: 'absent' | 'excused' | null;
}

interface RosterSession {
  id: string;
  title: string;
  session_type: string;
  scheduled_at: string;
  duration_minutes: number;
  audience: 'everyone' | 'assigned';
  cancelled: boolean;
  has_started: boolean;
  has_ended: boolean;
  points_reward: number;
}

type TabKey = 'all' | 'not_registered' | 'registered' | 'to_approve' | 'attended' | 'did_not_attend';

// Where a person stands now. A withdrawn registration counts as not registered.
// ("To approve" is not here: it is the part of Attended still waiting for the trainer.)
// "Registered" is wider than this map says: see hasRegistered.
const TAB_OF: Record<RosterState, Exclude<TabKey, 'all' | 'to_approve'>> = {
  not_registered: 'not_registered',
  cancelled: 'not_registered',
  registered: 'registered',
  attended: 'attended',
  absent: 'did_not_attend',
  excused: 'did_not_attend',
  no_show: 'did_not_attend',
};

// Everyone with a registration on record stays under Registered, whatever happened
// afterwards: somebody who registered and then attended is counted under Registered
// AND under Attended. (It used to mean "registered and nothing since", so the count
// dropped to 0 as attendance was saved, which read as registrations disappearing.)
const hasRegistered = (row: { state: RosterState }): boolean =>
  row.state !== 'not_registered' && row.state !== 'cancelled';

const TABS: Array<{ key: TabKey; label: string; icon: LucideIcon }> = [
  { key: 'all', label: 'All', icon: Users },
  { key: 'not_registered', label: 'Not registered', icon: UserMinus },
  { key: 'registered', label: 'Registered', icon: Clock },
  { key: 'to_approve', label: 'To approve', icon: ClipboardCheck },
  { key: 'attended', label: 'Attended', icon: UserCheck },
  { key: 'did_not_attend', label: 'Did not attend', icon: UserX },
];

const STATE_LABEL: Record<RosterState, string> = {
  not_registered: 'Not registered',
  registered: 'Registered',
  attended: 'Attended',
  absent: 'Absent',
  excused: 'Excused',
  // Registered, the session is over, and nobody checked in or marked them.
  no_show: 'Did not check in',
  cancelled: 'Registration cancelled',
};

const STATE_CLASS: Record<RosterState, string> = {
  not_registered: 'bg-gray-100 text-gray-700',
  registered: 'bg-blue-100 text-blue-700',
  attended: 'bg-green-100 text-green-700',
  absent: 'bg-red-100 text-red-700',
  excused: 'bg-amber-100 text-amber-700',
  no_show: 'bg-orange-100 text-orange-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

// The API sends timestamps as "2026-10-07 12:15:00+05:30"; not every browser
// parses the space-separated form.
const toDate = (value: string | null): Date | null => {
  if (!value) return null;
  const iso = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (!Number.isNaN(iso.getTime())) return iso;
  const loose = new Date(value);
  return Number.isNaN(loose.getTime()) ? null : loose;
};

const formatDateTime = (value: string | null): string => {
  const date = toDate(value);
  if (!date) return '';
  return `${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`;
};

const assignedViaText = (row: RosterRow): string =>
  row.assigned_via.length > 0 ? row.assigned_via.join(', ') : 'Self-registered';

const isAwaiting = (row: RosterRow): boolean => row.review === 'awaiting';
/** Whoever approved, rejected or marked it; "admin" when the server did not say. */
const decider = (row: RosterRow): string => row.decided_by_name || row.decided_by || 'admin';

/**
 * What the trainer had marked before this person checked in, or null: nothing was
 * marked first, or the server is an older one that does not say.
 */
const markedBefore = (row: RosterRow): 'absent' | 'excused' | null =>
  row.marked_before === 'absent' || row.marked_before === 'excused' ? row.marked_before : null;

// The tag beside the status, for as long as the check-in stands: while it waits and once
// it is approved. A rejected one is back at the mark, and the Attendance line tells that.
const MARKED_BEFORE_TAG: Record<'absent' | 'excused', string> = {
  absent: 'Was marked absent',
  excused: 'Was excused',
};

// The same, as it reads inside the Attendance line: the mark came first, the check-in after it.
const MARKED_BEFORE_ORDER: Record<'absent' | 'excused', string> = {
  absent: 'after being marked absent',
  excused: 'after being excused',
};

/** The tag's words for this row; empty when nothing was marked before the check-in, or the check-in was rejected. */
const markedBeforeTag = (row: RosterRow): string => {
  const before = markedBefore(row);
  return before && row.state === 'attended' ? MARKED_BEFORE_TAG[before] : '';
};

// The Status column in words, for the Excel file: the status, then the tag when the row has one.
const statusText = (row: RosterRow): string => {
  const tag = markedBeforeTag(row);
  return tag ? `${STATE_LABEL[row.state]} · ${tag}` : STATE_LABEL[row.state];
};

/**
 * What the banner above the table says about the check-ins waiting for approval.
 * Two groups. The plain ones come first: they are what "Approve all" approves.
 * The ones that followed an absent / excused mark are counted after them, and are
 * settled row by row.
 */
const awaitingBannerText = (plain: number, afterMark: number): string => {
  const parts: string[] = [];
  if (plain > 0) {
    parts.push(
      plain === 1
        ? '1 learner checked in on their phone and is waiting for your approval.'
        : `${plain} learners checked in on their phones and are waiting for your approval.`,
      'Approve each one, or reject it to mark them absent.',
    );
  }
  if (afterMark > 0) {
    if (plain > 0) {
      parts.push(
        `${afterMark} more had been marked absent or excused before they checked in.`,
        afterMark === 1
          ? 'Approve all leaves that one out: approve or reject it on its own row.'
          : 'Approve all leaves those out: approve or reject each on its own row.',
      );
    } else {
      // Nobody else is waiting, so there is no "more": the sentence stands on its own.
      parts.push(
        afterMark === 1
          ? '1 learner had been marked absent or excused before they checked in, and is waiting for your approval.'
          : `${afterMark} learners had been marked absent or excused before they checked in, and are waiting for your approval.`,
      );
      parts.push(afterMark === 1 ? 'Approve or reject that one on its own row.' : 'Approve or reject each of those on its own row.');
    }
  }
  return parts.join(' ');
};

// How an attendance was recorded and what the trainer made of it, in words; empty
// for anyone nobody has marked and who did not check in.
const attendanceText = (row: RosterRow): string => {
  const checkedIn = formatDateTime(row.check_in_at);
  const selfCheckIn = checkedIn ? `Self check-in, ${checkedIn}` : 'Self check-in';
  if (row.state === 'attended') {
    if (row.attendance_source === 'self') {
      if (row.review === 'awaiting') {
        // A check-in that followed the trainer's own mark says so, in the order it happened.
        const before = markedBefore(row);
        return before
          ? `${selfCheckIn} · ${MARKED_BEFORE_ORDER[before]} · awaiting approval`
          : `${selfCheckIn} · awaiting approval`;
      }
      if (row.review === 'approved') return `${selfCheckIn} · approved by ${decider(row)}`;
      return selfCheckIn;
    }
    const at = formatDateTime(row.attended_at);
    return at ? `Marked by ${decider(row)}, ${at}` : `Marked by ${decider(row)}`;
  }
  if (row.review === 'rejected') return `${selfCheckIn} · rejected by ${decider(row)}`;
  if ((row.state === 'absent' || row.state === 'excused') && row.decided_by) return `Marked by ${decider(row)}`;
  return '';
};

const sessionTypeLabel = (type: string): string => (type === 'classroom' ? 'Classroom' : 'Video Conference');

export default function LmsSessionLearnersPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;

  const [session, setSession] = useState<RosterSession | null>(null);
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [partial, setPartial] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const [search, setSearch] = useState('');
  const [markingAttendance, setMarkingAttendance] = useState(false);
  // Whose attendance is being saved right now ('*' = everyone waiting), and what went wrong last time.
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // `quiet` re-reads after a save without swapping the table for the loader.
  const fetchRoster = useCallback(async (quiet = false) => {
    if (!id || !projectId) return;
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const response = await lmsAPI.getSessionRoster(id, projectId);
      const data = response.data?.data;
      setSession(data?.session ?? null);
      setRows(data?.rows ?? []);
      setPartial(data?.partial === true);
      setTruncated(data?.truncated === true);
    } catch {
      if (quiet) {
        // The save itself worked; only reading the list again did not. Keep the
        // table and say so, instead of replacing it with "could not load".
        setSaveError('Saved, but the list could not be refreshed. Reload the page to see the latest.');
        return;
      }
      setError('Could not load the learners for this session. Please try again.');
      setSession(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [id, projectId]);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  /**
   * Approve / reject / mark: all one save, through the register's own endpoint.
   * 'attended' on a self check-in is the approval; 'absent' is the rejection.
   */
  const saveAttendance = async (codes: string[], status: 'attended' | 'absent', busyKey: string) => {
    if (!session || codes.length === 0 || saving) return;
    setSaving(busyKey);
    setSaveError(null);
    try {
      await lmsAPI.markSessionAttendance(session.id, codes.map((employee_code) => ({ employee_code, status })), projectId);
    } catch (err) {
      // The server's own words when it gave a reason (for example, the session has not started).
      const reason = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSaveError(reason || 'Could not save the attendance. Please try again.');
      setSaving(null);
      return;
    }
    try {
      await fetchRoster(true);
    } finally {
      setSaving(null);
    }
  };

  const markAttended = (row: RosterRow) => saveAttendance([row.employee_code], 'attended', row.employee_code);

  const markAbsent = (row: RosterRow) => {
    // Say what "absent" means before it happens: the points paid for attending go
    // back, and while the session is still on, what it does to checking in. With a
    // check-in on record the mark is the trainer rejecting it, and that is final.
    // With none, the learner may still check in once, and that check-in comes back
    // under To approve. After the session, marking somebody who never attended
    // takes nothing away and asks nothing.
    const points = row.state === 'attended' && row.points_awarded > 0
      ? ` Their ${row.points_awarded} ${row.points_awarded === 1 ? 'point' : 'points'} for this session will be taken back.`
      : '';
    const stillRunning = Boolean(session && !session.has_ended);
    const checkIn = !stillRunning
      ? ''
      : row.check_in_at
        ? ' This is final: they will not be able to check in again for this session.'
        : ' If they turn up, they can still check in once on their phone while the session is running, and it will come back to you under To approve.';
    if ((row.state === 'attended' || checkIn) && !window.confirm(`Mark ${row.employee_name} as absent?${points}${checkIn}`)) return;
    void saveAttendance([row.employee_code], 'absent', row.employee_code);
  };

  // Attendance is settled once the session has started (a session closed early
  // counts). Before that a slip of the hand would mark somebody absent days ahead,
  // for a session that has not happened yet.
  const canMark = Boolean(session && !session.cancelled && (session.has_started || session.has_ended));
  // Nothing waits for approval on a cancelled session: there is nothing to approve.
  const awaiting = useMemo(() => (session?.cancelled ? [] : rows.filter(isAwaiting)), [rows, session]);
  const isWaiting = (row: RosterRow): boolean => !session?.cancelled && isAwaiting(row);
  // "Approve all" takes only the plain check-ins. One that followed the trainer's
  // own absent / excused mark is theirs to look at on its own row: they marked the
  // person away a few minutes ago, and one click must not undo that unseen.
  const approvable = useMemo(() => awaiting.filter((row) => !markedBefore(row)), [awaiting]);
  const awaitingAfterMark = awaiting.length - approvable.length;
  const approveAll = () => saveAttendance(approvable.map((row) => row.employee_code), 'attended', '*');

  const counts = useMemo(() => {
    const out: Record<TabKey, number> = {
      all: rows.length, not_registered: 0, registered: 0, to_approve: awaiting.length, attended: 0, did_not_attend: 0,
    };
    for (const row of rows) {
      const now = TAB_OF[row.state];
      // Registered is counted for everyone who registered; the other tabs say where they stand now.
      if (now !== 'registered') out[now] += 1;
      if (hasRegistered(row)) out.registered += 1;
    }
    return out;
  }, [rows, awaiting]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (tab === 'to_approve') {
        if (!isWaiting(row)) return false;
      } else if (tab === 'registered') {
        if (!hasRegistered(row)) return false;
      } else if (tab !== 'all' && TAB_OF[row.state] !== tab) {
        return false;
      }
      if (!needle) return true;
      return row.employee_name.toLowerCase().includes(needle) || row.employee_code.toLowerCase().includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, tab, search, session]);

  const sort = useTableSort<RosterRow>(filtered, {
    employee: (r) => r.employee_name,
    code: (r) => r.employee_code,
    store: (r) => r.store || null,
    via: (r) => assignedViaText(r),
    registered: (r) => toDate(r.registered_at)?.getTime() ?? null,
    status: (r) => STATE_LABEL[r.state],
    attendance: (r) => attendanceText(r) || null,
    points: (r) => r.points_awarded,
  });

  // The table as shown: same rows, same order, same columns.
  const handleDownload = () => {
    if (!session) return;
    const headers = ['Employee', 'Employee Code', 'Store', 'Assigned via', 'Registered on', 'Status', 'Attendance', 'Points'];
    const body: TableCell[][] = sort.sorted.map((r) => [
      r.employee_name, r.employee_code, r.store, assignedViaText(r), formatDateTime(r.registered_at),
      statusText(r), attendanceText(r), r.points_awarded,
    ]);
    void downloadTableXlsx(
      `session-learners-${fileSlug(session.title)}-${new Date().toISOString().slice(0, 10)}`,
      { name: 'Learners', headers, rows: body },
    );
  };

  const subtitle = session ? (
    <>
      {session.title} · {formatDateTime(session.scheduled_at)} · {sessionTypeLabel(session.session_type)} ·{' '}
      {session.audience === 'assigned' ? 'Assigned learners only' : 'Open to everyone'}
    </>
  ) : (
    'Who was assigned, who registered and who attended'
  );

  return (
    <div className="space-y-6">
      <LmsEditorHeader
        onBack={() => navigate('/lms/sessions')}
        title="Session Learners"
        subtitle={subtitle}
        actions={
          session && !session.cancelled ? (
            <button
              type="button"
              onClick={() => setMarkingAttendance(true)}
              className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <CheckCircle className="h-4 w-4" />
              Mark attendance
            </button>
          ) : undefined
        }
      />

      {!projectId ? (
        <p className="py-16 text-center text-sm text-gray-500">Select a project to see this session.</p>
      ) : loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
          <InlineLoader label="Loading learners…" />
        </div>
      ) : error || !session ? (
        <p className="py-16 text-center text-sm text-red-600">{error ?? 'Session not found.'}</p>
      ) : (
        <>
          {session.cancelled && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
              This session was cancelled. The list shows where each person stood.
            </p>
          )}
          {session.audience === 'everyone' && (
            <p className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-800">
              This session is open to everyone in the project, so it has no assigned list. People appear here once
              they register, along with anyone it was also assigned to by name, role or journey.
            </p>
          )}
          {partial && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
              The statuses are complete, but some names and stores could not be loaded just now. Refresh in a minute
              to see them.
            </p>
          )}
          {!session.cancelled && !canMark && (
            <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
              Attendance can be marked here once the session has started.
            </p>
          )}
          {canMark && awaiting.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary-100 bg-primary-50 px-4 py-2 text-sm text-primary-900">
              <p>{awaitingBannerText(approvable.length, awaitingAfterMark)}</p>
              {/* No button when every check-in waiting followed a mark: there is
                  nothing it would approve. */}
              {approvable.length > 0 && (
                <button
                  type="button"
                  onClick={approveAll}
                  disabled={saving !== null}
                  className="flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving === '*' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                  Approve all ({approvable.length})
                </button>
              )}
            </div>
          )}
          {saveError && (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{saveError}</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-100 bg-gray-50 p-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const isActive = t.key === tab;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                      isActive ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {t.label} ({counts[t.key]})
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name or employee code"
                  aria-label="Search by name or employee code"
                  className="w-64 rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <button
                type="button"
                onClick={handleDownload}
                disabled={sort.sorted.length === 0}
                className="flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download Excel
              </button>
            </div>
          </div>

          <section className="space-y-4 rounded-lg border border-gray-100 bg-white p-6">
            {rows.length === 0 ? (
              <p className="py-16 text-center text-sm text-gray-500">
                {session.audience === 'assigned'
                  ? 'Nobody has been assigned to this session yet. Use Assign Learners on the Training Sessions page.'
                  : 'Nobody has registered for this session yet.'}
              </p>
            ) : filtered.length === 0 ? (
              <p className="py-16 text-center text-sm text-gray-500">
                {search.trim() ? `No one in this list matches "${search.trim()}".` : 'Nobody is in this group.'}
              </p>
            ) : (
              <>
                {truncated && (
                  <p className="text-xs text-gray-500">Showing the first {rows.length} people.</p>
                )}
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr className="whitespace-nowrap text-left text-xs font-medium text-gray-500">
                        <SortTh label="Employee" sortKey="employee" sort={sort} className="px-4 py-3" />
                        <SortTh label="Employee Code" sortKey="code" sort={sort} className="px-4 py-3" />
                        <SortTh label="Store" sortKey="store" sort={sort} className="px-4 py-3" />
                        <SortTh label="Assigned via" sortKey="via" sort={sort} className="px-4 py-3" title="How the session reached this person: by name, through a role, or through a learning journey" />
                        <SortTh label="Registered on" sortKey="registered" sort={sort} className="px-4 py-3" />
                        <SortTh label="Status" sortKey="status" sort={sort} className="px-4 py-3" />
                        <SortTh label="Attendance" sortKey="attendance" sort={sort} className="px-4 py-3" title="How the attendance was recorded: the learner's own check-in (and whether it has been approved), or marked by the trainer or an admin" />
                        <SortTh label="Points" sortKey="points" sort={sort} align="right" className="px-4 py-3" />
                        {/* Kept in view: the table is wider than most windows, and the
                            actions are the reason a trainer is here. */}
                        {canMark && (
                          <th scope="col" className="sticky right-0 z-10 whitespace-nowrap bg-gray-50 px-6 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sort.sorted.map((r) => (
                        <tr key={r.employee_code} className="group hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">{r.employee_name}</td>
                          <td className="whitespace-nowrap px-4 py-2 tabular-nums text-gray-600">{r.employee_code}</td>
                          <td className="px-4 py-2 text-gray-600">{r.store || '—'}</td>
                          <td className="px-4 py-2 text-gray-600">{assignedViaText(r)}</td>
                          <td className="whitespace-nowrap px-4 py-2 text-gray-600">{formatDateTime(r.registered_at) || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-2">
                            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${STATE_CLASS[r.state]}`}>
                              {STATE_LABEL[r.state]}
                            </span>
                            {/* Beside the status, where it cannot scroll out of sight
                                behind the Actions column on a narrow window. */}
                            {isWaiting(r) && (
                              <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                                Awaiting approval
                              </span>
                            )}
                            {/* The trainer had marked this person absent or excused, and they
                                checked in afterwards. Shown whatever became of the check-in
                                (waiting, approved or rejected): it is how the row got here. */}
                            {markedBeforeTag(r) && (
                              <span className="ml-1.5 inline-flex rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                                {markedBeforeTag(r)}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2 text-gray-600">{attendanceText(r) || '—'}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-600">{r.points_awarded > 0 ? r.points_awarded : '—'}</td>
                          {canMark && (
                            <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-6 py-2 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                              <TableActionGroup>
                                {/* Approving a self check-in and marking somebody present are the
                                    same save; the wording follows what the row is waiting for. */}
                                <TableActionButton
                                  action="approve"
                                  label={`${isAwaiting(r) ? 'Approve attendance' : 'Mark as attended'}: ${r.employee_name}`}
                                  title={isAwaiting(r) ? 'Approve attendance' : 'Mark as attended'}
                                  disabled={saving !== null || (r.state === 'attended' && !isAwaiting(r))}
                                  onClick={() => markAttended(r)}
                                />
                                <TableActionButton
                                  action="reject"
                                  label={`${r.state === 'attended' ? 'Reject attendance (mark as absent)' : 'Mark as absent'}: ${r.employee_name}`}
                                  title={r.state === 'attended' ? 'Reject attendance (mark as absent)' : 'Mark as absent'}
                                  disabled={saving !== null || r.state === 'absent'}
                                  onClick={() => markAbsent(r)}
                                />
                              </TableActionGroup>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        </>
      )}

      {markingAttendance && session && (
        <LmsSessionAttendanceModal
          sessionId={session.id}
          sessionTitle={session.title}
          projectId={projectId}
          onClose={() => setMarkingAttendance(false)}
          onSaved={() => fetchRoster(true)}
        />
      )}
    </div>
  );
}
